import React, { useMemo, useState } from 'react';
import { UploadCloud, ExternalLink } from 'lucide-react';
import SafeIframe from '../iframeEmbed/SafeIframe';
import {
  buildDataImporterEmbedUrl,
  ONBOARDING_DESTINATION_PRESETS,
  ONBOARDING_LOOKBACK_OPTIONS,
} from './buildDataImporterEmbedUrl';

/**
 * Embeddable RCM Data Upload step — ZIP 837/835 with lookback and destination routing.
 */
export default function OnboardingDataUploadPanel({
  organizationId,
  initialLookbackDays = 90,
  initialDestination = 'both',
  embedHeight = 520,
  onSkip,
  onContinue,
  onMarkComplete,
  showFullPageLink = true,
  compact = false,
}) {
  const [lookbackDays, setLookbackDays] = useState(initialLookbackDays);
  const [destination, setDestination] = useState(initialDestination);
  const [marking, setMarking] = useState(false);

  const iframeSrc = useMemo(
    () =>
      organizationId
        ? buildDataImporterEmbedUrl({
            organizationId,
            lookbackDays,
            destination,
          })
        : '',
    [organizationId, lookbackDays, destination]
  );

  const fullPagePath = useMemo(() => {
    const params = new URLSearchParams({
      module: 'x12-pipeline',
      lookback: String(lookbackDays),
      dest: destination,
    });
    if (organizationId) params.set('organization_id', organizationId);
    return `/practice-data?${params.toString()}`;
  }, [organizationId, lookbackDays, destination]);

  const runAction = async (action) => {
    setMarking(true);
    try {
      if (onMarkComplete) await onMarkComplete();
      action?.();
    } finally {
      setMarking(false);
    }
  };

  if (!organizationId) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        Select an organization before uploading revenue data.
      </div>
    );
  }

  return (
    <div className={compact ? 'space-y-4' : 'space-y-5'}>
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <p className="font-medium flex items-center gap-2">
          <UploadCloud className="w-5 h-5 shrink-0" />
          Bring your revenue data
        </p>
        <p className="mt-1 text-blue-800">
          Upload a ZIP of 837 and 835 files from the last 30–90 days. We recommend{' '}
          <strong>Both</strong> so Denials, ERA, and analytics populate on day one.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Lookback window</label>
          <div className="flex flex-wrap gap-2">
            {ONBOARDING_LOOKBACK_OPTIONS.map((days) => (
              <button
                key={days}
                type="button"
                onClick={() => setLookbackDays(days)}
                className={`px-3 py-1.5 text-sm rounded-md border ${
                  lookbackDays === days
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                }`}
              >
                {days} days
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Load to</label>
          <div className="flex flex-col gap-2">
            {Object.entries(ONBOARDING_DESTINATION_PRESETS).map(([key, preset]) => (
              <button
                key={key}
                type="button"
                onClick={() => setDestination(key)}
                className={`text-left rounded-md border px-3 py-2 text-sm ${
                  destination === key
                    ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600'
                    : 'border-gray-200 bg-white hover:border-blue-300'
                }`}
              >
                <span className="font-medium text-gray-900">{preset.label}</span>
                <span className="block text-xs text-gray-500">{preset.hint}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div
        className="rounded-lg border border-gray-200 overflow-hidden bg-white"
        style={{ height: embedHeight, minHeight: 360 }}
      >
        <SafeIframe
          src={iframeSrc}
          title="RCM Data Upload"
          serviceName="RCM Data Upload"
          loadingLabel="Loading upload workspace…"
        />
      </div>

      {showFullPageLink && (
        <p className="text-xs text-gray-500">
          <a
            href={fullPagePath}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
          >
            Open full upload page
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-2 sm:justify-between">
        <button
          type="button"
          onClick={() => runAction(onSkip)}
          disabled={marking}
          className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Skip — use demo data only
        </button>
        <button
          type="button"
          onClick={() => runAction(onContinue)}
          disabled={marking}
          className="px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {marking ? 'Saving…' : 'Continue — I uploaded or will upload later'}
        </button>
      </div>
    </div>
  );
}
